<script setup>

import {onMounted, onUnmounted, reactive, watch} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";
import {Permissions} from "@/Utils/Permissions";

const compName = 'ManageFederatedGroupComponent';

const props = defineProps({
  status: String,
  create: Boolean,
  update: Boolean,
  federatedGroupId: Number,
  updateNum: Number
});

const emit = defineEmits(['closed','update']);

const data = reactive({
  loading: true,
  dialog: true,
  editedItem: {
    name: "",
    groupId: "",
    isAdmin: false
  }
});

const close = () => {
  emit('closed');
}

const save = async () => {
  if(!data.loading && (props.create || props.update)) {
    data.loading = true;

    // Create new federated group
    if(props.create) {
      await ApiWrapper.post('federated-groups', 'create-group', {
            groupId: data.editedItem.groupId,
            name: data.editedItem.name,
            isAdmin: data.editedItem.isAdmin
          })
          .then((response) => {
            emit('update');
            close();
            ToastUtil.successMsg('Created federated group');
          })
          .catch((e) => {
            ToastUtil.error(e);
          })
          .finally(() => {
            data.loading = false;
          });
    }
    // Update existing federated group
    else if(props.update){
      await ApiWrapper.post('federated-groups', 'update-group', {
            id: props.federatedGroupId,
            groupId: data.editedItem.groupId,
            name: data.editedItem.name,
            isAdmin: data.editedItem.isAdmin
          })
          .then((response) => {
            emit('update');
            close();
            ToastUtil.successMsg('Updated federated group!');
          })
          .catch((e) => {
            ToastUtil.error(e);
          })
          .finally(() => {
            data.loading = false;
          });

      activeComponentStore.notifyComponents(['federated-groups'],compName);
    }
  }
}

watch(() => props.updateNum, async (val) => {
  clear();
  if(props.federatedGroupId) {
    await ApiWrapper.post('federated-groups', 'get-group', {
      id: props.federatedGroupId
    })
    .then((response) => {
      data.editedItem = {
        groupId: response.group.group_id,
        name: response.group.name,
        isAdmin: response.group.is_admin
      }
    })
    .catch((e) => {
      ToastUtil.error(e);
    })
    .finally(() => {
      data.loading = false;
    });
  }
});

const clear = () => {
  data.editedItem = {
    name: "",
    groupId: "",
    isAdmin: false
  };
}

onMounted(async ()=>{
  clear();
});

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});

</script>

<template>
  <div class="pa-4 text-center">
    <v-dialog
        v-if="props.status === 'open'"
        v-model="data.dialog"
        max-width="600"
        persistent
        @click:outside="close"
    >
      <v-card>
        <v-toolbar flat color="white">
          <v-toolbar-title>{{ props.create ? "Create": ""}} {{ props.update ? "Update" : "" }} Federated Group</v-toolbar-title>
          <v-progress-linear
              :active="data.loading"
              :indeterminate="data.loading"
              absolute
              bottom
              color="primary"
          >
          </v-progress-linear>
        </v-toolbar>

        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                  v-model="data.editedItem.name"
                  label="Name"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                  v-model="data.editedItem.groupId"
                  label="Federated ID"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                  class="mr-4"
                  label="Admin Group"
                  v-model="data.editedItem.isAdmin"
              >
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              text="Close"
              variant="plain"
              @click="close"
          ></v-btn>

          <v-btn
              color="primary"
              text="Update"
              variant="tonal"
              :disabled="data.loading"
              @click="save"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

</style>